import { useEffect, useMemo, useState } from 'react';
import { statements } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { users } from '../services/api';
import LayoutV2 from '../components/LayoutV2';
import {
  Button,
  Loading,
  PageHeader,
  SectionBlock
} from '../components/ui';

function getRecentSevenDaysRange() {
  const now = new Date();
  const endDate = now.toISOString().slice(0, 10);
  const start = new Date(now);
  start.setDate(start.getDate() - 7);
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate
  };
}

function formatCurrency(value) {
  return `₱${Number(value || 0).toLocaleString()}`;
}

function formatDateTime(value) {
  if (!value) return '--';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function validateGcashNumber(value) {
  const normalized = String(value || '').trim();
  if (!normalized) return '';
  if (!/^09\d{9}$/.test(normalized)) {
    return 'GCash number must start with 09 and contain exactly 11 digits';
  }
  return '';
}

export default function StatementsV2() {
  const { isAdmin, user } = useAuth();
  const [list, setList] = useState([]);
  const [filters, setFilters] = useState(() => ({
    period: '',
    status: '',
    username: '',
    ...(isAdmin ? getRecentSevenDaysRange() : { startDate: '', endDate: '' })
  }));
  const [loading, setLoading] = useState(true);

  const [selectedStatement, setSelectedStatement] = useState(null);
  const [items, setItems] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [adjForm, setAdjForm] = useState({ amount: '', reason: '' });

  const [showSettle, setShowSettle] = useState(false);
  const [settleFilters, setSettleFilters] = useState(() => ({ ...getRecentSevenDaysRange(), username: '' }));
  const [unsettledList, setUnsettledList] = useState([]);
  const [selectedEarningIds, setSelectedEarningIds] = useState([]);
  const [loadingSettleList, setLoadingSettleList] = useState(false);
  const [submittingSettle, setSubmittingSettle] = useState(false);
  const [gcashForm, setGcashForm] = useState({ gcashName: '', gcashNumber: '' });
  const [gcashFeedback, setGcashFeedback] = useState({ type: '', text: '' });

  useEffect(() => {
    const loadStatements = async () => {
      setLoading(true);
      try {
        const res = await statements.list(filters);
        setList(res.data.statements || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadStatements();
  }, [filters]);

  useEffect(() => {
    if (isAdmin || !user?.id) return;

    const loadGcash = async () => {
      try {
        const gcashRes = await users.getGcash(user.id);
        setGcashForm({
          gcashName: String(gcashRes.data.gcashName || ''),
          gcashNumber: String(gcashRes.data.gcashNumber || '')
        });
      } catch (err) {
        console.error('Failed to load GCash:', err);
      }
    };

    loadGcash();
  }, [isAdmin, user?.id]);

  const loadStatementDetail = async (id) => {
    try {
      const res = await statements.get(id);
      setSelectedStatement(res.data.statement);
      setItems(res.data.items || []);
      if (isAdmin) {
        const adjRes = await statements.getAdjustments(id);
        setAdjustments(adjRes.data.adjustments || []);
      } else {
        setAdjustments([]);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleMarkPaid = async (id) => {
    if (!window.confirm('Mark this statement as paid?')) return;
    try {
      await statements.markPaid(id);
      const res = await statements.list(filters);
      setList(res.data.statements || []);
      setSelectedStatement(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed');
    }
  };

  const handleAddAdjustment = async () => {
    if (!selectedStatement) return;
    if (!adjForm.amount || !adjForm.reason.trim()) {
      alert('Amount and reason are required');
      return;
    }
    try {
      await statements.addAdjustment(selectedStatement.id, {
        amount: Number(adjForm.amount),
        currency: 'PHP',
        reason: adjForm.reason.trim(),
        description: adjForm.reason.trim()
      });
      setAdjForm({ amount: '', reason: '' });
      await loadStatementDetail(selectedStatement.id);
      const res = await statements.list(filters);
      setList(res.data.statements || []);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add adjustment');
    }
  };

  const handleDeleteAdjustment = async (adjId) => {
    if (!selectedStatement) return;
    if (!window.confirm('Delete this adjustment item?')) return;
    try {
      await statements.deleteAdjustment(selectedStatement.id, adjId);
      await loadStatementDetail(selectedStatement.id);
      const res = await statements.list(filters);
      setList(res.data.statements || []);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete adjustment');
    }
  };

  const openSettle = async () => {
    setShowSettle(true);
    setSelectedEarningIds([]);
    setLoadingSettleList(true);
    try {
      const res = await statements.listUnsettledEarnings(settleFilters);
      setUnsettledList(res.data.earnings || []);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to load unsettled earnings');
    } finally {
      setLoadingSettleList(false);
    }
  };

  const loadUnsettledEarnings = async () => {
    setLoadingSettleList(true);
    try {
      const res = await statements.listUnsettledEarnings(settleFilters);
      setUnsettledList(res.data.earnings || []);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to load unsettled earnings');
    } finally {
      setLoadingSettleList(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedEarningIds((prev) => (
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    ));
  };

  const toggleSelectAll = () => {
    if (selectedEarningIds.length === unsettledList.length) {
      setSelectedEarningIds([]);
      return;
    }
    setSelectedEarningIds(unsettledList.map((e) => e.id));
  };

  const handleSettleSelected = async () => {
    if (!selectedEarningIds.length) {
      alert('Please select at least one earning item');
      return;
    }

    setSubmittingSettle(true);
    try {
      await statements.settle({
        earningIds: selectedEarningIds,
        startDate: settleFilters.startDate,
        endDate: settleFilters.endDate
      });
      setShowSettle(false);
      setSelectedEarningIds([]);
      const res = await statements.list(filters);
      setList(res.data.statements || []);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to settle selected earnings');
    } finally {
      setSubmittingSettle(false);
    }
  };

  const earningItems = useMemo(
    () => items.filter((item) => String(item.type).toLowerCase() === 'earnings'),
    [items]
  );
  const earningTotal = earningItems.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const adjustmentTotal = adjustments.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const previewTotal = earningTotal + adjustmentTotal;

  const saveMyGcash = async () => {
    if (!user?.id) return;
    const gcashNumberError = validateGcashNumber(gcashForm.gcashNumber);
    if (gcashNumberError) {
      setGcashFeedback({ type: 'error', text: gcashNumberError });
      return;
    }

    try {
      const res = await users.updateGcash(user.id, gcashForm);
      setGcashForm({
        gcashName: String(res.data.gcashName || ''),
        gcashNumber: String(res.data.gcashNumber || '')
      });
      setGcashFeedback({ type: 'success', text: 'GCash saved successfully.' });
    } catch (err) {
      setGcashFeedback({
        type: 'error',
        text: err.response?.data?.error || 'Failed to save GCash'
      });
    }
  };

  return (
    <LayoutV2>
      <PageHeader
        title="Statements"
        actions={
          isAdmin ? (
            <Button type="button" variant="primary" onClick={openSettle}>
              Settle
            </Button>
          ) : null
        }
      />

      <div className="space-y-6">
        {!isAdmin ? (
          <SectionBlock
            title="My GCash Account"
            actions={
              <Button type="button" variant="primary" onClick={saveMyGcash}>
                Save GCash
              </Button>
            }
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="label">Account name</label>
                <input
                  value={gcashForm.gcashName}
                  onChange={(e) => {
                    setGcashForm((prev) => ({ ...prev, gcashName: e.target.value }));
                    setGcashFeedback({ type: '', text: '' });
                  }}
                  placeholder="Account name"
                  className="input"
                />
              </div>
              <div>
                <label className="label">GCash number</label>
                <input
                  value={gcashForm.gcashNumber}
                  onChange={(e) => {
                    setGcashForm((prev) => ({ ...prev, gcashNumber: e.target.value }));
                    setGcashFeedback({ type: '', text: '' });
                  }}
                  placeholder="09xxxxxxxxx"
                  className="input"
                />
              </div>
            </div>
            {gcashFeedback.text ? (
              <p className={`mt-3 text-sm ${gcashFeedback.type === 'success' ? 'text-emerald-200' : 'text-red-200'}`}>
                {gcashFeedback.text}
              </p>
            ) : null}
          </SectionBlock>
        ) : null}

        <SectionBlock
          title="Filters"
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            {isAdmin ? (
              <>
                <div>
                  <label className="label">Username / Display Name</label>
                  <input
                    value={filters.username}
                    onChange={(e) => setFilters((prev) => ({ ...prev, username: e.target.value }))}
                    placeholder="Search username/display name"
                    className="input"
                  />
                </div>
                <div>
                  <label className="label">Start Date</label>
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => setFilters((prev) => ({ ...prev, startDate: e.target.value }))}
                    className="input"
                  />
                </div>
                <div>
                  <label className="label">End Date</label>
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => setFilters((prev) => ({ ...prev, endDate: e.target.value }))}
                    className="input"
                  />
                </div>
              </>
            ) : (
              <div>
                <label className="label">Period</label>
                <select
                  value={filters.period}
                  onChange={(e) => setFilters((prev) => ({ ...prev, period: e.target.value }))}
                  className="input"
                >
                  <option value="">All Periods</option>
                  {[...new Set(list.map((s) => s.period))].map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="label">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
                className="input"
              >
                <option value="">All Status</option>
                <option value="PENDING_CONFIRM">Pending Confirm</option>
                <option value="PAID">Paid</option>
              </select>
            </div>

            {isAdmin ? (
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full justify-center"
                  onClick={() => setFilters((prev) => ({ ...prev, username: '', status: '', ...getRecentSevenDaysRange() }))}
                >
                  Reset 7 Days
                </Button>
              </div>
            ) : null}
          </div>
        </SectionBlock>

        <SectionBlock
          title="Statements"
        >
          {loading ? (
            <Loading text="Loading statements..." />
          ) : list.length === 0 ? (
            <div className="rounded-[20px] border border-dashed border-white/10 bg-slate-950/20 p-6 text-sm text-slate-400">
              No statements found.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {list.map((stmt) => (
                <div key={stmt.id} className="rounded-[20px] border border-white/8 bg-white/[0.03] p-4 text-sm">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="min-w-0">
                      <p className="font-medium text-slate-100">{stmt.display_name} · {stmt.period}</p>
                      <p className="mt-1 text-slate-400">
                        Orders: {stmt.total_orders} · {formatCurrency(stmt.net_earnings)} · {stmt.status}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" variant="secondary" size="sm" onClick={() => loadStatementDetail(stmt.id)}>
                        View
                      </Button>
                      {isAdmin && stmt.status === 'PENDING_CONFIRM' ? (
                        <Button type="button" variant="primary" size="sm" onClick={() => handleMarkPaid(stmt.id)}>
                          Mark Paid
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionBlock>
      </div>

      {showSettle ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3">
          <div className="max-h-[90vh] w-full max-w-5xl overflow-auto rounded-[28px] border border-white/10 bg-[#0f172a] p-5">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-50">Settle Unsettled Earnings</h2>
              <Button type="button" variant="ghost" onClick={() => setShowSettle(false)}>
                Close
              </Button>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <input type="date" value={settleFilters.startDate} onChange={(e) => setSettleFilters((p) => ({ ...p, startDate: e.target.value }))} className="input" />
              <input type="date" value={settleFilters.endDate} onChange={(e) => setSettleFilters((p) => ({ ...p, endDate: e.target.value }))} className="input" />
              <input value={settleFilters.username} onChange={(e) => setSettleFilters((p) => ({ ...p, username: e.target.value }))} placeholder="Filter username/display name" className="input" />
              <Button type="button" variant="secondary" onClick={loadUnsettledEarnings}>
                Search
              </Button>
            </div>

            {loadingSettleList ? (
              <Loading text="Loading unsettled earnings..." />
            ) : (
              <>
                <div className="mb-3 mt-4 flex items-center justify-between text-sm">
                  <div className="text-slate-300">Selected: {selectedEarningIds.length}/{unsettledList.length}</div>
                  <Button type="button" variant="ghost" size="sm" onClick={toggleSelectAll}>
                    {selectedEarningIds.length === unsettledList.length ? 'Clear All' : 'Select All'}
                  </Button>
                </div>

                <div className="table-container">
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Pick</th>
                          <th>User</th>
                          <th>Order</th>
                          <th>Loan</th>
                          <th>Amount</th>
                          <th>Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {unsettledList.map((row) => (
                          <tr key={row.id}>
                            <td>
                              <input type="checkbox" checked={selectedEarningIds.includes(row.id)} onChange={() => toggleSelect(row.id)} />
                            </td>
                            <td>{row.displayName} @{row.username}</td>
                            <td>{row.orderId}</td>
                            <td>{row.loanName || '--'}</td>
                            <td>{formatCurrency(row.earningAmount)}</td>
                            <td>{formatDateTime(row.orderTime)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {unsettledList.length === 0 ? (
                  <div className="mt-3 text-sm text-slate-400">No unsettled earnings in the current filter.</div>
                ) : null}

                <div className="mt-4 flex justify-end">
                  <Button
                    type="button"
                    variant="primary"
                    disabled={submittingSettle || selectedEarningIds.length === 0}
                    onClick={handleSettleSelected}
                  >
                    {submittingSettle ? 'Settling...' : 'Generate Statements'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}

      {selectedStatement ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3">
          <div className="max-h-[88vh] w-full max-w-3xl overflow-auto rounded-[28px] border border-white/10 bg-[#0f172a] p-5">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-50">Statement Details</h2>
              <Button type="button" variant="ghost" onClick={() => setSelectedStatement(null)}>
                Close
              </Button>
            </div>

            <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="surface-card"><p className="label">Statement Net</p><p className="text-2xl font-semibold text-emerald-200">{formatCurrency(selectedStatement.net_earnings)}</p></div>
              <div className="surface-card"><p className="label">Preview Total</p><p className="text-2xl font-semibold text-slate-50">{formatCurrency(previewTotal)}</p></div>
            </div>

            {isAdmin && selectedStatement.status === 'PENDING_CONFIRM' ? (
              <SectionBlock
                title="Add Adjustment"
                className="mb-4"
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Amount"
                    value={adjForm.amount}
                    onChange={(e) => setAdjForm((prev) => ({ ...prev, amount: e.target.value }))}
                    className="input"
                  />
                  <input type="text" value="PHP" disabled className="input text-slate-500" />
                  <input
                    type="text"
                    placeholder="Reason"
                    value={adjForm.reason}
                    onChange={(e) => setAdjForm((prev) => ({ ...prev, reason: e.target.value }))}
                    className="input"
                  />
                </div>
                <div className="mt-4">
                  <Button type="button" variant="primary" onClick={handleAddAdjustment}>
                    Add Adjustment
                  </Button>
                </div>
              </SectionBlock>
            ) : null}

            <div className="space-y-3">
              {earningItems.map((item) => (
                <div key={item.id} className="rounded-[20px] border border-white/8 bg-white/[0.03] p-4">
                  <p className="text-sm font-medium text-slate-100">earnings · {item.order_id || '-'}</p>
                  <p className="mt-1 text-sm text-slate-400">{formatCurrency(item.amount)}</p>
                </div>
              ))}

              {adjustments.map((adj) => (
                <div key={adj.id} className="flex items-center justify-between gap-3 rounded-[20px] border border-cyan-700/60 bg-cyan-500/5 p-4">
                  <div>
                    <p className="text-sm font-medium text-slate-100">adjustment · {formatCurrency(adj.amount)} · {adj.currency || 'PHP'}</p>
                    <p className="mt-1 text-sm text-slate-400">{adj.reason || adj.description}</p>
                  </div>
                  {isAdmin && selectedStatement.status === 'PENDING_CONFIRM' ? (
                    <Button type="button" variant="ghost" size="sm" onClick={() => handleDeleteAdjustment(adj.id)}>
                      Delete
                    </Button>
                  ) : null}
                </div>
              ))}

              {earningItems.length === 0 && adjustments.length === 0 ? (
                <div className="text-sm text-slate-400">No line items.</div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </LayoutV2>
  );
}
